import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { ArrowRight, Check, Globe, ClipboardCheck, FileCheck, ShieldCheck, BarChart3, Users, Star, Settings, MoveRight, AlertTriangle, Eye, Database, Layers, Search } from 'lucide-react';
import { LaptopFrame, PhoneFrame, VendorDashboard } from '@/components/DeviceMockups';

/* ---- DATA ---- */

const sqLifecycle = [
  { label: 'Onboard', sub: 'Registration, documentation, PPAP', icon: Users, desc: 'Supplier registration, profile creation, document submission, initial assessment, and PPAP review process.' },
  { label: 'Audit', sub: 'VDA 6.3-style process audits', icon: ClipboardCheck, desc: 'Process audits aligned to VDA 6.3, audit checklists, NC tracking, corrective action follow-up, and scoring.' },
  { label: 'Score', sub: 'Ratings, PPM, delivery, quality', icon: Star, desc: 'Supplier scorecards based on PPM, delivery performance, quality metrics, responsiveness, and overall rating.' },
  { label: 'Govern', sub: 'Complaints, CAPA, certificates', icon: ShieldCheck, desc: 'Complaint management, CAPA tracking, certificate monitoring, compliance status, and escalation workflows.' },
  { label: 'Monitor', sub: 'Dashboards, alerts, compliance', icon: BarChart3, desc: 'Real-time dashboards, compliance alerts, trend analysis, audit readiness, and vendor risk visibility.' },
];

const capabilities = [
  { icon: Users, label: 'Supplier onboarding & documentation' },
  { icon: ClipboardCheck, label: 'Supplier profile management' },
  { icon: FileCheck, label: 'Certificate management & compliance' },
  { icon: ShieldCheck, label: 'Supplier complaints and CAPA' },
  { icon: Settings, label: 'Supplier audits (VDA 6.3-style)' },
  { icon: Globe, label: 'RFQ / LOI / approval flows' },
  { icon: Star, label: 'Supplier scorecards & ratings' },
  { icon: BarChart3, label: 'Compliance dashboards & visibility' },
];

const capaFlowSteps = [
  { step: 'Complaint Logged', desc: 'Complaint registered with part, batch, and issue details' },
  { step: 'Containment Action', desc: 'Immediate containment instructions sent to supplier' },
  { step: 'Root Cause Analysis', desc: 'Supplier submits RCA with evidence' },
  { step: 'CAPA Plan', desc: 'Corrective and preventive action plan approval' },
  { step: 'Implementation', desc: 'Supplier implements corrective actions' },
  { step: 'Verification & Closure', desc: 'SQE verifies effectiveness and closes complaint' },
];

const detailedFeatures = [
  { title: 'Supplier Onboarding & Documentation', icon: Users, items: ['Vendor development workflow', 'Supplier PPAP / APQP submissions', 'Digital document submission and tracking', 'Initial assessment and approval'] },
  { title: 'RFQ to LOI Management', icon: Globe, items: ['Digital RFQ release to suppliers', 'Techno-commercial proposal submission', 'L1 supplier ranking and comparisons', 'Digital LOI awarding and approval'] },
  { title: 'Supplier Audit Management', icon: ClipboardCheck, items: ['VDA 6.3-style audit plan vs actual tracking', 'Audit intimation, alerts, and checklists', 'NC tracking and closure workflows', 'Process and product audit plans'] },
  { title: 'Supplier Ratings & Dashboards', icon: Star, items: ['Real-time supplier quality ratings', 'PPM, delivery, and performance dashboards', '4M monitoring and CAPA status tracking', 'Compliance overview for IATF audits'] },
  { title: 'BOM & Material Management', icon: Layers, items: ['BOM stock management synced with ERP', 'BOM costing sheet generation and CFT approvals', 'Approved supplier management per part', 'RI parts and DOL tracking'] },
  { title: 'Project Development Tracking', icon: Settings, items: ['Supplier development checkpoint tracking', 'Activity status notifications and milestones', 'Real-time visibility into supplier progress', 'Supplier portal access for self-service'] },
];

const vendorScorecardData = [
  { name: 'Supplier A', quality: 95, delivery: 88, response: 92, overall: 91, status: 'Approved' },
  { name: 'Supplier B', quality: 78, delivery: 92, response: 85, overall: 84, status: 'Conditional' },
  { name: 'Supplier C', quality: 99, delivery: 96, response: 94, overall: 96, status: 'Preferred' },
  { name: 'Supplier D', quality: 65, delivery: 72, response: 60, overall: 66, status: 'Under Review' },
];

/* ---- COMPONENTS ---- */

function Section({ children, className = '', ...props }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`} {...props}>{children}</section>;
}

/* Supplier CAPA Flow Diagram */
function SupplierCAPAFlowDiagram() {
  return (
    <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-2xl p-6 lg:p-8">
      <h3 className="text-sm font-bold text-[#111827] mb-6">Supplier Complaint & CAPA Workflow</h3>
      <div className="flex flex-col lg:flex-row items-center gap-3 lg:gap-0">
        {capaFlowSteps.map((step, i) => (
          <div key={i} className="flex items-center gap-3 lg:gap-0 w-full lg:w-auto">
            <div className="flex flex-col items-center text-center min-w-[100px] max-w-[120px]">
              <div className="w-10 h-10 rounded-lg bg-white border-2 border-[#6B7280]/20 flex items-center justify-center shadow-sm">
                <span className="text-sm font-bold text-[#6B7280]">{i + 1}</span>
              </div>
              <p className="text-[10px] font-bold text-[#111827] mt-2">{step.step}</p>
              <p className="text-[9px] text-[#6B7280] leading-tight mt-0.5">{step.desc}</p>
            </div>
            {i < capaFlowSteps.length - 1 && (
              <div className="hidden lg:flex items-center flex-1 min-w-[20px] px-0.5">
                <div className="h-0.5 flex-1 bg-gradient-to-r from-[#6B7280]/20 to-[#6B7280]/40" />
                <MoveRight className="w-3 h-3 text-[#6B7280]/50 -ml-0.5" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Vendor Scorecard Visual */
function VendorScorecardVisual() {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#F3F4F6]">
        <Star className="w-4 h-4 text-[#6B7280]" />
        <span className="text-sm font-bold text-[#111827]">Supplier Scorecard</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              <th className="py-2 text-left text-[#6B7280] font-medium">Supplier</th>
              <th className="py-2 text-center text-[#6B7280] font-medium">Quality</th>
              <th className="py-2 text-center text-[#6B7280] font-medium">Delivery</th>
              <th className="py-2 text-center text-[#6B7280] font-medium">Response</th>
              <th className="py-2 text-center text-[#6B7280] font-medium">Overall</th>
              <th className="py-2 text-center text-[#6B7280] font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {vendorScorecardData.map((v, i) => (
              <tr key={i} className="border-b border-[#F3F4F6]">
                <td className="py-2 font-medium text-[#111827]">{v.name}</td>
                {[v.quality, v.delivery, v.response, v.overall].map((val, j) => (
                  <td key={j} className="py-2 text-center">
                    <span className={`font-bold ${val >= 90 ? 'text-emerald-600' : val >= 80 ? 'text-amber-600' : 'text-red-500'}`}>{val}%</span>
                  </td>
                ))}
                <td className="py-2 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${v.status === 'Preferred' ? 'bg-emerald-50 text-emerald-700' : v.status === 'Approved' ? 'bg-blue-50 text-blue-700' : v.status === 'Conditional' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{v.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- PAGE ---- */

export default function MushinNexusPage() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="w-2 h-12 rounded-full bg-[#6B7280] mb-6" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="nexus-title">Mushin Vendor Connect</h1>
          <p className="text-lg text-[#C41E3A] font-medium mt-3">Supplier quality management per VDA 6.3 and IATF expectations</p>
          <p className="mt-6 text-[#374151] max-w-2xl leading-relaxed">
            Mushin Vendor Connect helps manufacturers control supplier quality workflows: onboarding, audits, complaints, CAPA, certificates, scorecards, and compliance visibility across the entire vendor ecosystem. Built for SQE, purchase, and central quality teams.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="nexus-demo-cta">Book a Demo <ArrowRight className="w-4 h-4" /></Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="nexus-quote-cta">Get a Quote</a>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Dashboard Preview</p>
              <h2 className="font-heading text-3xl font-bold tracking-tight text-[#111827]">Supplier quality at a glance</h2>
              <p className="mt-4 text-[#374151] leading-relaxed">Monitor supplier scorecards, VDA 6.3 audit status, certificate compliance, CAPA tracking, and vendor quality KPIs from a centralized dashboard built for SQE teams.</p>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-lg shadow-black/[0.04]">
              <img src="https://static.prod-images.emergentagent.com/jobs/b2b63064-eca2-488c-8792-d404db0409a2/images/df9a88f67fe016b20b117905100ff2bc0d417841625b9ac6e09f18320e8ad6c9.png" alt="Mushin Vendor Connect Dashboard" className="w-full h-auto" loading="lazy" data-testid="vendor-connect-dashboard-img" />
            </div>
          </div>
        </div>
      </Section>

      {/* ========== ENHANCED SUPPLIER LIFECYCLE ========== */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]" data-testid="sq-lifecycle-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Supplier Quality Lifecycle</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]">End-to-end supplier quality governance</h2>
          <p className="mt-5 text-lg text-[#374151] max-w-2xl">From first contact to ongoing compliance, every stage of supplier relationship is digitized and governed.</p>
          {/* Lifecycle Flow */}
          <div className="mt-12 relative">
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#6B7280]/20 via-[#6B7280]/40 to-[#6B7280]/20 z-0" />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 relative z-10">
              {sqLifecycle.map((step, i) => (
                <div key={i} className="flex flex-col items-center text-center group" data-testid={`sq-step-${i}`}>
                  <div className="w-16 h-16 rounded-2xl bg-white border-2 border-[#E5E7EB] group-hover:border-[#6B7280]/40 flex items-center justify-center mb-3 shadow-sm transition-colors">
                    <step.icon className="w-7 h-7 text-[#6B7280]" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] font-mono text-[#6B7280] uppercase tracking-wider mb-1">Step {i + 1}</span>
                  <h4 className="font-heading text-sm font-bold text-[#111827] mb-1">{step.label}</h4>
                  <p className="text-[11px] text-[#6B7280] leading-snug mb-2">{step.sub}</p>
                  <p className="text-[10px] text-[#374151] leading-relaxed bg-white border border-[#E5E7EB] rounded-lg p-3 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ========== SUPPLIER COMPLAINT / CAPA FLOW ========== */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white" data-testid="capa-flow-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Complaint & CAPA Workflow</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]">Structured supplier complaint resolution</h2>
          <p className="mt-4 text-[#374151] max-w-3xl leading-relaxed">
            From complaint logging to containment, root cause analysis, CAPA plan, implementation, and verified closure. Mushin structures every step of supplier complaint resolution with audit trail.
          </p>
          <div className="mt-10">
            <SupplierCAPAFlowDiagram />
          </div>
        </div>
      </Section>

      {/* ========== VENDOR SCORECARD ========== */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]" data-testid="vendor-scorecard-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Vendor Scorecards</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]">Supplier performance at a glance</h2>
          <p className="mt-5 text-lg text-[#374151] max-w-2xl">Quality, delivery, response, and overall scores for every supplier, with real-time status and approval tracking.</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <VendorScorecardVisual />
            <div className="space-y-4">
              {[
                { icon: Star, title: 'Multi-dimensional Scoring', desc: 'Score suppliers across quality (PPM), delivery, responsiveness, audit scores, and compliance metrics.' },
                { icon: AlertTriangle, title: 'Risk-Based Classification', desc: 'Preferred, Approved, Conditional, and Under Review. Automatic classification based on performance thresholds.' },
                { icon: Eye, title: 'Real-Time Dashboards', desc: 'Live supplier quality dashboards with trend analysis, compliance alerts, and comparative vendor views.' },
                { icon: ShieldCheck, title: 'IATF Audit Readiness', desc: 'Supplier quality evidence organized and accessible for IATF 16949 and VDA 6.3 audit requirements.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white border border-[#E5E7EB] rounded-xl p-4">
                  <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-[#6B7280]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-[15px] font-semibold text-[#111827]">{item.title}</h4>
                    <p className="text-xs text-[#374151] leading-relaxed mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Capabilities */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Capabilities</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]">Full-spectrum supplier quality control</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {capabilities.map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg px-4 py-4 shadow-sm group hover:border-[#6B7280]/30 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center flex-shrink-0 group-hover:bg-[#6B7280]/10 transition-colors">
                  <c.icon className="w-4 h-4 text-[#6B7280]" strokeWidth={1.5} />
                </div>
                <span className="text-[15px] text-[#374151]">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Feature Depth */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Feature Depth</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827]">How Mushin Vendor Connect works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {detailedFeatures.map((f, i) => (
              <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm" data-testid={`nexus-feature-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-[#F3F4F6] flex items-center justify-center mb-3">
                  <f.icon className="w-4 h-4 text-[#6B7280]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-semibold text-[#111827] mb-4">{f.title}</h3>
                <ul className="space-y-2">
                  {f.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-[#374151]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#6B7280] mt-1.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ERP Integration */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white" data-testid="nexus-erp-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">ERP Integration</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#111827]">Connect supplier data to enterprise systems</h2>
          <p className="mt-5 text-lg text-[#374151] max-w-2xl">Mushin Vendor Connect supports integration with SAP and Oracle for supplier master sync, PO linkage, quality data push, and inspection result sharing.</p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {['Supplier master sync', 'PO / GRN linkage', 'Quality rejection data', 'BOM costing integration'].map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg px-4 py-3">
                <Database className="w-3.5 h-3.5 text-[#6B7280] flex-shrink-0" strokeWidth={2} />
                <span className="text-[15px] text-[#374151]">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Platform Screens */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white" data-testid="vendor-platform-screens">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Platform Experience</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#111827]">Vendor management across every device</h2>
          <p className="mt-5 text-lg text-[#374151] max-w-2xl">SQE teams manage suppliers from desktop dashboards. Suppliers submit documents and respond to complaints from any device.</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8 items-end">
            <div className="lg:col-span-3">
              <LaptopFrame>
                <VendorDashboard />
                <div className="px-3 pb-2">
                  <div className="bg-[#F9FAFB] rounded p-2 border border-[#F3F4F6]">
                    <div className="text-[7px] text-[#9CA3AF] uppercase mb-1">Upcoming Actions</div>
                    {['Supplier B: CAPA due in 3 days', 'Supplier D: Audit scheduled', 'Certificate expiry: Supplier A (15 days)'].map((a, i) => (
                      <div key={i} className="flex items-center gap-1.5 py-0.5 text-[8px]">
                        <div className={`w-1 h-1 rounded-full ${i === 0 ? 'bg-[#C41E3A]' : 'bg-[#EA580C]'}`} />
                        <span className="text-[#4B5563]">{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </LaptopFrame>
            </div>
            <div className="lg:col-span-2 max-w-[200px] mx-auto lg:mx-0">
              <PhoneFrame>
                <div className="p-2.5 text-[9px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-[#111827] text-[10px]">Supplier Portal</span>
                    <span className="text-[7px] px-1.5 py-0.5 bg-[#ECFDF5] text-[#059669] rounded font-medium">Active</span>
                  </div>
                  {['Upload PPAP docs', 'Submit CAPA response', 'View audit findings', 'Update certificates'].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 py-1.5 border-b border-[#F3F4F6] last:border-0">
                      <div className="w-3.5 h-3.5 rounded bg-[#F9FAFB] flex items-center justify-center">
                        <span className="text-[7px] text-[#C41E3A] font-bold">{i + 1}</span>
                      </div>
                      <span className="text-[#374151]">{item}</span>
                    </div>
                  ))}
                </div>
              </PhoneFrame>
            </div>
          </div>
        </div>
      </Section>

      {/* Pricing */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-[#111827] mb-6">Pricing approach</h2>
          <p className="text-[#374151] leading-relaxed mb-6">Mushin Vendor Connect pricing is tailored based on your specific supplier governance requirements:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['Active supplier count', 'Audit and workflow depth', 'Rollout scope and compliance complexity', 'Enterprise governance needs'].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-[#E5E7EB] rounded-lg px-4 py-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
                <span className="text-[15px] text-[#374151]">Depends on {item.toLowerCase()}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-24 text-center border-t border-[#F0F0F0] bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-[#111827]">Govern your automotive supplier ecosystem</h2>
          <p className="mt-4 text-[#374151]">Get visibility and structured control across your entire vendor network per IATF and VDA expectations.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="nexus-final-demo">Book a Demo</Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="nexus-final-quote">Get a Quote</a>
          </div>
        </div>
      </section>
    </div>
  );
}
